/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 21 2023
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

// class UserAdminSpace extends BaseService {
// 	constructor() {
// 		super();
// 		this.initialize();
// 	}

// 	private APIPath = (groupId: string, path?: string) =>
// 		`/api/admin/groups/${groupId}/spaces${path ? `/${path}` : ""}`;

// 	private static Cache = {
// 		Delete: atom<WebServices.UserAdmin.Space.Delete.Response>({}),
// 		Search: atom<WebServices.UserAdmin.Space.Search.Response>({}),
// 		DeleteCancel: atom<WebServices.UserAdmin.Space.DeleteCancel.Response>({})
// 	};

// 	public deleteSpace = (name: string, events: API.Events) => ({
// 		name,
// 		events,
// 		callback: ({ groupId, spaceId }: WebServices.UserAdmin.Space.Delete.Request) =>
// 			this.delete(this.APIPath(groupId, spaceId)),
// 		cache: UserAdminSpace.Cache.Delete
// 	});

// 	public search = (name: string, events: API.Events) => ({
// 		name,
// 		events,
// 		callback: (parameter: WebServices.UserAdmin.Space.Search.Request) => this.get(this.APIPath(parameter.groupId)),
// 		cache: UserAdminSpace.Cache.Search
// 	});

// 	public deleteCancel = (name: string, events: API.Events) => ({
// 		name,
// 		events,
// 		callback: (parameter: WebServices.UserAdmin.Space.DeleteCancel.Request) =>
// 			this.get(this.APIPath(parameter.groupId)),
// 		cache: UserAdminSpace.Cache.DeleteCancel
// 	});
// }

// class UserAdminSpaceUser extends BaseService {
// 	constructor() {
// 		super();
// 		this.initialize();
// 	}

// 	private APIPath = (groupId: string, spaceId: string) =>
// 		`/api/admin/groups/${groupId}/spaces/${spaceId}/space_users`;

// 	private static Cache = {
// 		List: atom<WebServices.UserAdmin.SpaceUser.List.Response>({})
// 	};

// 	public list = (name: string, events: API.Events) => ({
// 		name,
// 		events,
// 		callback: (parameter: WebServices.UserAdmin.SpaceUser.List.Request) =>
// 			this.get(this.APIPath(parameter.groupId, parameter.spaceId), parameter),
// 		cache: UserAdminSpaceUser.Cache.List
// 	});
// }

class Space extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (spaceId?: string, path?: string) =>
		`/api/spaces${spaceId ? `/${spaceId}` : ""}${path ? `/${path}` : ""}`;

	private ActionAPIPath = () => `/api/action_logs/space`;

	private static Cache = {
		Delete: atom<WebServices.Space.Delete.Response>({}),
		Detail: atom<WebServices.Space.Detail.Response>({}),
		Create: atom<WebServices.Space.Create.Response>({}),
		Update: atom<WebServices.Space.Update.Response>({}),
		CheckBeforeParticipate: atom<WebServices.Space.CheckBeforeParticipate.Response>({}),
		Participate: atom<WebServices.Space.Participate.Response>({}),
		Leave: atom<WebServices.Space.Leave.Response>({}),
		Authorities: atom<WebServices.Space.Authorities.Response>({}),
		mySpaces: {
			List: atom<WebServices.Space.MySpaces.List.Response>({})
		},
		Search: atom<WebServices.Space.Search.Response>({}),
		AllowTypes: atom<WebServices.Space.Search.AllowTypes.Response>({}),
		join: {
			List: atom<WebServices.Space.Join.List.Response>({}),
			Request: atom<WebServices.Space.Join.Request.Response>({}),
			Process: atom<WebServices.Space.Join.Process.Response>({}),
			Status: atom<WebServices.Space.Join.Status.Response>({}),
			Cancel: atom<WebServices.Space.Join.Cancel.Response>({})
		},
		ResistExternalGroupUser: atom<WebServices.Space.ResistExternalGroupUser.Response>({}),
		InquiryWOAuth: atom<WebServices.Space.InquiryWOAuth.Response>({}),
		SpaceActionLog: atom<WebServices.Space.SpaceActionLog.Response>({})
	};

	public deleteSpace = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceIds }: WebServices.Space.Delete.Request) => this.delete(this.APIPath(), { spaceIds }),
		cache: Space.Cache.Delete
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.Space.Detail.Request) => this.get(this.APIPath(spaceId)),
		cache: Space.Cache.Detail
	});

	public create = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ ...rest }: WebServices.Space.Create.Request) => this.post(this.APIPath(), rest),
		cache: Space.Cache.Create
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.Space.Update.Request) => this.put(this.APIPath(spaceId), rest),
		cache: Space.Cache.Update
	});
	public checkBeforeParticipate = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.Space.CheckBeforeParticipate.Request) =>
			this.get(this.APIPath(spaceId, "participate")),
		cache: Space.Cache.CheckBeforeParticipate
	});

	public enter = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.Space.Participate.Request) =>
			this.post(this.APIPath(spaceId, "participate"), rest),
		cache: Space.Cache.Participate
	});

	public leave = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...parameter }: WebServices.Space.Leave.Request) =>
			this.post(this.APIPath(spaceId, "leave"), parameter),
		cache: Space.Cache.Leave
	});

	public authorities = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.Space.Authorities.Request) =>
			this.get(this.APIPath(spaceId, `authorities`)),
		cache: Space.Cache.Authorities
	});

	public mySpaces = {
		list: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.Space.MySpaces.List.Request) =>
				this.get(this.APIPath("my_spaces"), parameter),
			cache: Space.Cache.mySpaces.List
		})
	};

	public search = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Space.Search.Request) => this.get(this.APIPath(), parameter),
		cache: Space.Cache.Search
	});

	public allowTypes = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Space.Search.AllowTypes.Request = {}) =>
			this.get(this.APIPath("search_allow_types"), parameter),
		cache: Space.Cache.AllowTypes
	});

	public resistExternalGroupUser = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, userId }: WebServices.Space.ResistExternalGroupUser.Request) =>
			this.post(this.APIPath(spaceId, `external_users/${userId}`)),
		cache: Space.Cache.ResistExternalGroupUser
	});

	public join = {
		list: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId }: WebServices.Space.Join.List.Request) =>
				this.get(this.APIPath(spaceId, "join_requests")),
			cache: Space.Cache.join.List
		}),
		request: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId }: WebServices.Space.Join.Request.Request) =>
				this.post(this.APIPath(spaceId, "join_requests")),
			cache: Space.Cache.join.Request
		}),
		process: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId, ...rest }: WebServices.Space.Join.Process.Request) =>
				this.post(this.APIPath(spaceId, "join_requests/process"), rest),
			cache: Space.Cache.join.Process
		}),
		status: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId }: WebServices.Space.Join.Status.Request) =>
				this.get(this.APIPath(spaceId, "join_status")),
			cache: Space.Cache.join.Status
		}),
		cancel: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId }: WebServices.Space.Join.Cancel.Request) =>
				this.post(this.APIPath(spaceId, "join_requests/cancel")),
			cache: Space.Cache.join.Cancel
		})
	};

	public inquiryWOAuth = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.Space.InquiryWOAuth.Request) => this.get(this.APIPath(spaceId, "simple")),
		cache: Space.Cache.InquiryWOAuth
	});

	public log = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Space.SpaceActionLog.Request) => this.get(this.ActionAPIPath(), parameter),
		cache: Space.Cache.SpaceActionLog
	});
}

class SpaceInvite extends BaseService {
	constructor() {
		super();
	}
	private InviteAPIPath = (id: string) => `/api/space_invite_emails/${id}`;
	private APIPath = (spaceId: string, path?: string) => `/api/spaces/${spaceId}${path ? `/${path}` : ""}`;

	private static Cache = {
		Cancel: atom<WebServices.SpaceInvite.Cancel.Response>({}),
		List: atom<WebServices.SpaceInvite.List.Response>({}),
		Inquiry: atom<WebServices.SpaceInvite.Inquiry.Response>({}),
		Invite: atom<WebServices.SpaceInvite.Invite.Response>({}),
		Process: atom<WebServices.SpaceInvite.Process.Response>({}),
		Guest: {
			Renew: atom<WebServices.SpaceInvite.Guest.Renew.Response>({}),
			Inquiry: atom<WebServices.SpaceInvite.Guest.Inquiry.Response>({}),
			InquiryByKey: atom<WebServices.SpaceInvite.Guest.InquiryByKey.Response>({})
		}
	};

	public cancel = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Cancel.Request) =>
			this.delete(this.APIPath(spaceId, "space_invite"), rest),
		cache: SpaceInvite.Cache.Cancel
	});

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.SpaceInvite.List.Request) =>
			this.get(this.APIPath(spaceId, "space_invite")),
		cache: SpaceInvite.Cache.List
	});

	public inquiry = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ id }: WebServices.SpaceInvite.Inquiry.Request) => this.get(this.InviteAPIPath(id)),
		cache: SpaceInvite.Cache.Inquiry
	});

	public invite = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Invite.Request) =>
			this.post(this.APIPath(spaceId, "space_invite"), rest),
		cache: SpaceInvite.Cache.Invite
	});

	public process = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Process.Request) =>
			this.post(this.APIPath(spaceId, "space_invite/process"), rest),
		cache: SpaceInvite.Cache.Process
	});

	public guest = {
		renew: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Guest.Renew.Request) =>
				this.post(this.APIPath(spaceId, "guest_invite/renew"), rest),
			cache: SpaceInvite.Cache.Guest.Renew
		}),
		inquiry: (name: string, events: API.Events) => ({
			name,
			events,
			callback: ({ spaceId, ...rest }: WebServices.SpaceInvite.Guest.Inquiry.Request) =>
				this.get(this.APIPath(spaceId, "guest_invite/code"), rest),
			cache: SpaceInvite.Cache.Guest.Inquiry
		}),
		inquiryByKey: (name: string, events: API.Events) => ({
			name,
			events,
			callback: (parameter: WebServices.SpaceInvite.Guest.InquiryByKey.Request) =>
				this.get(this.APIPath("guest_invite_code"), parameter),
			cache: SpaceInvite.Cache.Guest.InquiryByKey
		})
	};
}

class SpaceKeyword extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (spaceId: string, keywordId: string) => `/api/spaces/${spaceId}/keywords/${keywordId}`;

	private static Cache = {
		Delete: atom<WebServices.SpaceKeyword.Delete.Response>({})
	};

	public deleteKeyword = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, keywordId }: WebServices.SpaceKeyword.Delete.Request) =>
			this.delete(this.APIPath(spaceId, keywordId)),
		cache: SpaceKeyword.Cache.Delete
	});
}

class SpaceRecommend extends BaseService {
	constructor() {
		super();
	}

	private APIPath = "/api/spaces/recommends";

	private static Cache = {
		List: atom<WebServices.SpaceRecommend.List.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.SpaceRecommend.List.Request) => this.get(this.APIPath, parameter),
		cache: SpaceRecommend.Cache.List
	});
}

class SpaceTheme extends BaseService {
	constructor() {
		super();
	}

	private APIPath = "/api/space_themes";

	private static Cache = {
		List: atom<WebServices.SpaceTheme.List.Response>({}),
		Random: atom<WebServices.SpaceTheme.GetRandom.Response>({})
	};

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.SpaceTheme.List.Request) => this.get(this.APIPath, parameter),
		cache: SpaceTheme.Cache.List
	});

	public random = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.SpaceTheme.GetRandom.Request) =>
			this.get(`${this.APIPath}/random`, parameter),
		cache: SpaceTheme.Cache.Random
	});
}

class SpaceUser extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (spaceId: string, path?: string) => `/api/spaces/${spaceId}/space_users${path ? `/${path}` : ""}`;

	private static Cache = {
		Leave: atom<WebServices.SpaceUser.Leave.Response>({}),
		LeaveList: atom<WebServices.SpaceUser.LeaveList.Response>({}),
		LeaveProcess: atom<WebServices.SpaceUser.LeaveProcess.Response>({}),
		RemoveMember: atom<WebServices.SpaceUser.RemoveMember.Response>({}),
		List: atom<WebServices.SpaceUser.List.Response>({}),
		Participants: atom<WebServices.SpaceUser.Participants.Response>({}),
		Pin: atom<WebServices.SpaceUser.Pin.Response>({}),
		Notificaiton: atom<WebServices.SpaceUser.Notification.Response>({}),
		Appointment: atom<WebServices.SpaceUser.Appointment.Response>({}),
		JoinStatus: atom<WebServices.SpaceUser.JoinStatus.Response>({})
	};

	public leave = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.SpaceUser.Leave.Request) => this.delete(this.APIPath(spaceId)),
		cache: SpaceUser.Cache.Leave
	});

	public leaveList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.SpaceUser.LeaveList.Request) =>
			this.get(this.APIPath(spaceId, "delete_requests")),
		cache: SpaceUser.Cache.LeaveList
	});

	public leaveProcess = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceUser.LeaveProcess.Request) =>
			this.post(this.APIPath(spaceId, "delete_requests/process"), rest),
		cache: SpaceUser.Cache.LeaveProcess
	});

	public removeMember = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, userId }: WebServices.SpaceUser.RemoveMember.Request) =>
			this.delete(this.APIPath(spaceId, userId)),
		cache: SpaceUser.Cache.RemoveMember
	});

	public list = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.SpaceUser.List.Request) => this.get(this.APIPath(spaceId)),
		cache: SpaceUser.Cache.List
	});

	public participants = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.SpaceUser.Participants.Request) =>
			this.get(this.APIPath(spaceId, "participants")),
		cache: SpaceUser.Cache.Participants
	});

	public pin = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceUser.Pin.Request) =>
			this.put(this.APIPath(spaceId, "pin"), rest),
		cache: SpaceUser.Cache.Pin
	});

	public notification = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.SpaceUser.Notification.Request) =>
			this.put(this.APIPath(spaceId, "notification"), rest),
		cache: SpaceUser.Cache.Notificaiton
	});

	public appointment = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, userId, ...rest }: WebServices.SpaceUser.Appointment.Request) =>
			this.put(this.APIPath(spaceId, `admin/${userId}`), rest),
		cache: SpaceUser.Cache.Appointment
	});

	public joinStatus = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, userId }: WebServices.SpaceUser.JoinStatus.Request) =>
			this.get(this.APIPath(spaceId, userId + "/join_status")),
		cache: SpaceUser.Cache.JoinStatus
	});
}

class SpaceNotification extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (spaceId: string) => `/api/spaces/${spaceId}/space_chat_notification`;

	private static Cache = {
		SpaceChatNotification: atom<WebServices.Space.SpaceChatNotification.Response>({})
	};

	public UpdateSpaceChatNotification = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, ...rest }: WebServices.Space.SpaceChatNotification.Request) =>
			this.post(this.APIPath(spaceId), rest),
		cache: SpaceNotification.Cache.SpaceChatNotification
	});
}

class SpacePublicOpenWorld extends BaseService {
	constructor() {
		super();
	}
	private APIPath = (path?: string) => `/api/open_worlds${path ? `/${path}` : ""}`;

	private static Cache = {
		InquiryOpenworldList: atom<WebServices.Space.InqueryPublicOpenWorldInfoList.Response>({}),
		InquiryOpenworldInfo: atom<WebServices.Space.InquiryParticipateOpenworld.Response>({}),
		CheckBeforeParticipateOpenworld: atom<WebServices.Space.CheckBeforeParticipateOpenworld.Response>({}),
		Participate: atom<WebServices.Space.ParticipateOpenworld.Response>({})
	};
	public inquiryOpenworldList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.Space.InqueryPublicOpenWorldInfoList.Request) =>
			this.get(this.APIPath(), parameter),
		cache: SpacePublicOpenWorld.Cache.InquiryOpenworldList
	});

	public inquiryOpenworldInfo = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ ...rest }: WebServices.Space.InquiryParticipateOpenworld.Request) =>
			this.get(this.APIPath("participate"), rest),
		cache: SpacePublicOpenWorld.Cache.InquiryOpenworldInfo
	});
	public checkBeforeParticipateOpenworld = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ ...rest }: WebServices.Space.CheckBeforeParticipateOpenworld.Request) =>
			this.get(this.APIPath("participate"), rest),
		cache: SpacePublicOpenWorld.Cache.CheckBeforeParticipateOpenworld
	});
	public participate = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ ...rest }: WebServices.Space.ParticipateOpenworld.Request) =>
			this.post(this.APIPath("participate"), rest),
		cache: SpacePublicOpenWorld.Cache.Participate
	});
}

class SpaceServiceStatic {
	constructor(
		public readonly userAdmin = {
			// space: new UserAdminSpace(),
			// spaceUser: new UserAdminSpaceUser()
		},
		public readonly space = new Space(),
		public readonly spaceInvite = new SpaceInvite(),
		public readonly spaceKeyword = new SpaceKeyword(),
		public readonly spaceRecommend = new SpaceRecommend(),
		public readonly spaceTheme = new SpaceTheme(),
		public readonly spaceUser = new SpaceUser(),
		public readonly spaceNotification = new SpaceNotification(),
		public readonly spacePublicOpenWorld = new SpacePublicOpenWorld()
	) {}
}
const SpaceService = new SpaceServiceStatic();
export default SpaceService;
